import api from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SupplierItemLink {
  _id: string;
  venueId?: string;
  inventoryItemId?: string;
  supplierId: string;
  supplierName: string | null;
  supplierReference?: string;
  supplierItemCode?: string;
  isPreferred: boolean;
  lastReceivedCost?: number;
  lastReceivedDate?: string;
  active: boolean;
  createdAt?: string;
}

export interface SupplierItemLinkPrefill {
  suggestedSupplierId: string | null;
  suggestedSupplierName?: string | null;
  suggestedCost: number | null;
  /** COSTING.1a-web (E3): the supplier link's lastReceivedDate (ISO), set only
   *  when suggestedCost came from a supplier link (not the balance fallback).
   *  Powers the "last paid X on <date>" caption under the cost field. */
  suggestedCostDate?: string | null;
}

// ── Service ───────────────────────────────────────────────────────────────────

const base = (venueId: string, itemId: string) =>
  `/admin/venue/${venueId}/inventory/item/${itemId}/supplier-link`;

export const getLinks = async (
  venueId: string,
  itemId: string,
): Promise<SupplierItemLink[]> => {
  const response = await api.get(base(venueId, itemId));
  return response.data;
};

export const createLink = async (
  venueId: string,
  itemId: string,
  payload: { supplierId: string; supplierReference?: string; supplierItemCode?: string },
): Promise<SupplierItemLink> => {
  const response = await api.post(base(venueId, itemId), payload);
  return response.data;
};

export const updateLink = async (
  venueId: string,
  itemId: string,
  linkId: string,
  payload: { supplierReference?: string; supplierItemCode?: string | null; active?: boolean },
): Promise<SupplierItemLink> => {
  const response = await api.put(`${base(venueId, itemId)}/${linkId}`, payload);
  return response.data;
};

export const setPreferred = async (
  venueId: string,
  itemId: string,
  linkId: string,
): Promise<SupplierItemLink> => {
  const response = await api.post(`${base(venueId, itemId)}/${linkId}/set-preferred`);
  return response.data;
};

export const clearPreferred = async (
  venueId: string,
  itemId: string,
  linkId: string,
): Promise<SupplierItemLink> => {
  const response = await api.post(`${base(venueId, itemId)}/${linkId}/clear-preferred`);
  return response.data;
};

/** Get all item links for a supplier (supplier-side view, includes wineDisplay). */
export const getLinksBySupplier = async (supplierId: string): Promise<(SupplierItemLink & {
  wineDisplay?: { name: string; displayVintage: string; producerName?: string; color?: string } | null;
  bottleSize?: number | null;
})[]> => {
  const response = await api.get(`/admin/supplier/${supplierId}/item-links`);
  return response.data;
};

export const getPrefill = async (
  venueId: string,
  itemId: string,
  supplierId?: string,
): Promise<SupplierItemLinkPrefill> => {
  const params: Record<string, string> = {};
  if (supplierId) params.supplierId = supplierId;
  const response = await api.get(`${base(venueId, itemId)}/prefill`, { params });
  return response.data;
};
